/**
 * Face boxes for the automatic page crop, from MediaPipe's BlazeFace running
 * in the browser (WebAssembly, CPU). Detection only: boxes, no identities,
 * nothing leaves the device.
 *
 * The runtime (~4MB compressed) and model are served from
 * /mediapipe/<version> and loaded once, lazily. warmFaceDetector() kicks that off when the photos
 * step opens; detectFaces() waits a bounded time for it and returns null
 * when it is not available, so the caller can fall back to the centre crop
 * rather than hold up a batch.
 */

/**
 * Must match the installed @mediapipe/tasks-vision version; the build script
 * (scripts/copyMediapipeRuntime.js) checks and refuses otherwise. The version
 * is in the path so the runtime can be cached for a long time.
 */
export const MEDIAPIPE_RUNTIME_VERSION = '1.1.1';
const WASM_BASE_PATH = `/mediapipe/${MEDIAPIPE_RUNTIME_VERSION}`;
/** Full-range BlazeFace: for rear-camera photos where faces can be small. */
const MODEL_PATH = `${WASM_BASE_PATH}/blaze_face_full_range.tflite`;
/**
 * Low on purpose: the crop only uses confident faces (see the crop helper),
 * but weaker candidates are kept so the review rules can see people the
 * crop may have missed, e.g. a wide group in dim light.
 */
const MIN_CONFIDENCE = 0.3;

let loadPromise = null;
let detector = null;
let failed = false;

/** Start loading the detector; safe to call many times. Never rejects. */
export function warmFaceDetector() {
  if (!loadPromise) {
    loadPromise = (async () => {
      const { FilesetResolver, FaceDetector } = await import('@mediapipe/tasks-vision');
      const vision = await FilesetResolver.forVisionTasks(WASM_BASE_PATH);
      detector = await FaceDetector.createFromOptions(vision, {
        baseOptions: { modelAssetPath: MODEL_PATH, delegate: 'CPU' },
        runningMode: 'IMAGE',
        minDetectionConfidence: MIN_CONFIDENCE,
      });
      return detector;
    })().catch((err) => {
      failed = true;
      console.warn('Face detector unavailable; auto-crop will centre instead:', err);
      return null;
    });
  }
  return loadPromise;
}

export const isFaceDetectorReady = () => !!detector;

/**
 * Faces in `source` (a canvas or image) as {x, y, width, height, score} in
 * its pixels. [] when there are none; null when the detector is not
 * available within `waitMs`.
 */
export async function detectFaces(source, { waitMs = 0 } = {}) {
  if (!detector && !failed) {
    const loading = warmFaceDetector();
    if (waitMs > 0) {
      await Promise.race([loading, new Promise((r) => setTimeout(r, waitMs))]);
    }
  }
  if (!detector) return null;
  const result = detector.detect(source);
  return (result?.detections || [])
    .map((d) => {
      const b = d.boundingBox;
      if (!b) return null;
      const score = d.categories?.[0]?.score ?? 1;
      return { x: b.originX, y: b.originY, width: b.width, height: b.height, score };
    })
    .filter(Boolean);
}

/** Test hook. */
export function __resetFaceDetectorForTests() {
  loadPromise = null;
  detector = null;
  failed = false;
}
